import { alpha, createTheme } from '@mui/material/styles';

/**
 * Light, airy theme shared with the agent website: soft grey canvas, white cards with a
 * soft shadow, one deep teal-blue accent. Chart colors live in `chartColors`.
 */
const TOKENS = {
  light: {
    page: '#f5f7fa',
    surface: '#ffffff',
    subtle: '#eef2f6',
    ink: '#0f172a',
    inkSecondary: '#475569',
    inkMuted: '#94a3b8',
    border: 'rgba(15, 23, 42, 0.08)',
    grid: '#e2e8f0',
    accent: '#0b6e8f',
    accentContrast: '#ffffff',
    shadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.12)',
  },
  dark: {
    page: '#0a101d',
    surface: '#0e1626',
    subtle: '#16203a',
    ink: '#e5ebf3',
    inkSecondary: '#aab6c8',
    inkMuted: '#6b7a90',
    border: 'rgba(255, 255, 255, 0.08)',
    grid: '#1f2a3d',
    accent: '#38bdd8',
    accentContrast: '#04101f',
    shadow: '0 1px 2px rgba(0, 0, 0, 0.3), 0 8px 24px -12px rgba(0, 0, 0, 0.5)',
  },
};

/** Validated categorical + status steps (see dataviz reference palette). */
export const chartColors = {
  light: { series: ['#2a78d6', '#eb6834', '#1baf7a'], good: '#0ca30c', critical: '#d03b3b', warning: '#fab219' },
  dark: { series: ['#3987e5', '#d95926', '#199e70'], good: '#0ca30c', critical: '#d03b3b', warning: '#fab219' },
};

/** @param {'light' | 'dark'} mode */
export function createAppTheme(mode) {
  const c = TOKENS[mode];

  return createTheme({
    palette: {
      mode,
      primary: { main: c.accent, contrastText: c.accentContrast },
      secondary: { main: c.inkSecondary },
      error: { main: '#d03b3b' },
      warning: { main: '#c98500' },
      success: { main: mode === 'light' ? '#006300' : '#0ca30c' },
      info: { main: mode === 'light' ? '#2a78d6' : '#3987e5' },
      background: { default: c.page, paper: c.surface },
      text: { primary: c.ink, secondary: c.inkSecondary, disabled: c.inkMuted },
      divider: c.border,
      action: {
        hover: alpha(c.ink, 0.04),
        selected: alpha(c.accent, 0.1),
      },
    },
    custom: c,
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: ['"Plus Jakarta Sans"', 'Inter', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Roboto', 'Arial', 'sans-serif'].join(','),
      fontSize: 13.5,
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 700, letterSpacing: '-0.015em', fontSize: '1.5rem' },
      h6: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.05rem' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
      overline: { fontWeight: 500, letterSpacing: '0.06em', lineHeight: 1.6 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: { WebkitFontSmoothing: 'antialiased', fontFeatureSettings: '"cv11", "ss01"' },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
      MuiCard: {
        defaultProps: { variant: 'elevation' },
        styleOverrides: { root: { borderRadius: 16, border: 'none', boxShadow: c.shadow } },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10, paddingInline: 16 },
          outlined: { borderColor: c.border, color: c.ink, '&:hover': { borderColor: alpha(c.ink, 0.25), backgroundColor: alpha(c.ink, 0.03) } },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: c.surface,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: alpha(c.ink, 0.12) },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(c.ink, 0.3) },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.accent, borderWidth: 1, boxShadow: `0 0 0 3px ${alpha(c.accent, 0.15)}` },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { '&.Mui-focused': { color: c.accent } } } },
      MuiMenu: {
        styleOverrides: {
          paper: { border: `1px solid ${c.border}`, borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' },
        },
      },
      MuiPopover: {
        styleOverrides: { paper: { border: `1px solid ${c.border}`, boxShadow: '0 8px 24px rgba(0,0,0,0.08)' } },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 20, border: 'none', boxShadow: '0 24px 64px rgba(15,23,42,0.22)' } },
      },
      MuiDialogTitle: { styleOverrides: { root: { fontSize: '1rem', fontWeight: 600 } } },
      MuiDialogContent: { styleOverrides: { dividers: { borderColor: c.border } } },
      MuiAlert: {
        styleOverrides: { root: { borderRadius: 10, border: `1px solid ${c.border}` } },
      },
      MuiChip: {
        styleOverrides: { root: { borderRadius: 8, fontWeight: 600 }, sizeSmall: { height: 22, fontSize: 12 } },
      },
      MuiTooltip: {
        styleOverrides: { tooltip: { backgroundColor: c.ink, color: c.surface, fontSize: 12, borderRadius: 6 } },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            border: `1px solid ${c.border}`,
            paddingBlock: 4,
            paddingInline: 12,
            color: c.inkSecondary,
            '&.Mui-selected': { backgroundColor: alpha(c.accent, 0.1), color: c.accent },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: c.border, paddingBlock: 10 },
          head: { color: c.inkMuted, fontWeight: 600, fontSize: 12 },
        },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { borderRadius: 99, backgroundColor: c.subtle } },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: 'none',
            '--DataGrid-containerBackground': c.subtle,
            '--DataGrid-rowBorderColor': c.border,
            '& .MuiDataGrid-columnHeaderTitle': { color: c.inkSecondary, fontWeight: 600, fontSize: 12 },
            '& .MuiDataGrid-row:hover': { backgroundColor: alpha(c.accent, 0.04) },
            '& .MuiDataGrid-columnSeparator': { color: c.border },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
            '& .MuiDataGrid-footerContainer': { borderColor: c.border },
          },
        },
      },
    },
  });
}
